import { FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/LanguageContext';
import { openPrintWindow } from '@/lib/printWindow';
import { PIVOT_FORM_COLS, getFormStartDate, formTypeLabel, monthLabel, cycleOf } from './historyUtils';

// Pivot view: rows = employee / department / month, columns = the 5 form types.
export function HistoryPivot({ submissions, pivotRow, setPivotRow, onDrillDown, filterType, filterDept, filterMonth }) {
  const { t, lang } = useT();

  const cycleLabelFromDate = (refStr) => {
    if (!refStr) return null;
    const d = new Date(refStr);
    if (isNaN(d.getTime())) return null;
    const [y, m] = cycleOf(d).split('-').map(Number);
    return monthLabel(t, y, m);
  };

  const rowKeyOf = (sub) => {
    if (pivotRow === 'department') return sub.department || t('hist.unset');
    if (pivotRow === 'month') return cycleLabelFromDate(getFormStartDate(sub) || sub.created_at) || t('hist.noDate');
    // employee — split combined names so multi-person submissions count per person
    const raw = (sub.employee_name || t('hist.unset')).toString();
    return raw.split(/[、,]/).map((s) => s.trim()).filter(Boolean);
  };

  const buildPivot = () => {
    const rowMap = new Map(); // rowKey → { [formType]: count, _total: count }
    submissions.forEach((sub) => {
      const keys = rowKeyOf(sub);
      const keyList = Array.isArray(keys) ? keys : [keys];
      keyList.forEach((k) => {
        if (!rowMap.has(k)) {
          const init = { _total: 0 };
          PIVOT_FORM_COLS.forEach((c) => { init[c.value] = 0; });
          rowMap.set(k, init);
        }
        const row = rowMap.get(k);
        row[sub.form_type] = (row[sub.form_type] || 0) + 1;
        row._total += 1;
      });
    });
    const colTotals = { _total: 0 };
    PIVOT_FORM_COLS.forEach((c) => { colTotals[c.value] = 0; });
    rowMap.forEach((row) => {
      PIVOT_FORM_COLS.forEach((c) => { colTotals[c.value] += row[c.value] || 0; });
      colTotals._total += row._total || 0;
    });
    const rows = Array.from(rowMap.entries())
      .map(([key, data]) => ({ key, ...data }))
      .sort((a, b) => b._total - a._total || String(a.key).localeCompare(String(b.key), 'ja'));
    return { rows, colTotals };
  };

  const pivot = buildPivot();

  // 月次まとめ PDF (print the current pivot table as a clean A4 sheet)
  const exportPivotPdf = () => {
    if (pivot.rows.length === 0) {
      toast.error(t('hist.pivot.noData'));
      return;
    }
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    const rowLabel = pivotRow === 'employee' ? t('hist.pivot.applicant') : pivotRow === 'department' ? t('hist.pivot.dept') : t('hist.pivot.month');
    let cycleLabel = t('hist.pdf.allPeriods');
    if (filterMonth !== 'all') {
      const [y, m] = filterMonth.split('-').map(Number);
      const prevM = m === 1 ? 12 : m - 1;
      cycleLabel = t('hist.pdf.cycleLabel', { y, m, m2: String(m).padStart(2, '0'), pm: prevM });
    }
    const typeLabel = filterType === 'all' ? t('hist.pdf.allTypes') : formTypeLabel(t, filterType);
    const deptLabel = filterDept === 'all' ? t('hist.pdf.allDepts') : filterDept;
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const gen = `${now.getFullYear()}/${pad(now.getMonth() + 1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const headCols = PIVOT_FORM_COLS.map((c) => `<th class="num">${esc(t(c.key))}</th>`).join('');
    const bodyRows = pivot.rows.map((r) => {
      const tds = PIVOT_FORM_COLS.map((c) => `<td class="num">${r[c.value] ? r[c.value] : '<span class="z">·</span>'}</td>`).join('');
      return `<tr><th class="rk">${esc(r.key)}</th>${tds}<td class="num tot">${r._total}</td></tr>`;
    }).join('');
    const footCols = PIVOT_FORM_COLS.map((c) => `<td class="num">${pivot.colTotals[c.value] || 0}</td>`).join('');

    const html = `<!DOCTYPE html><html lang="${lang === 'en' ? 'en' : 'ja'}"><head><meta charset="utf-8">
<title>${esc(t('hist.pdf.docTitle'))}_${esc(cycleLabel)}</title>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'IBM Plex Sans JP','Yu Gothic','Hiragino Sans',sans-serif; color:#1f2937; margin:0; }
  .brand { font-size:11px; font-weight:700; color:#0052FF; letter-spacing:.04em; }
  h1 { font-size:22px; margin:2px 0 6px; color:#111827; }
  .meta { font-size:11px; color:#6b7280; line-height:1.7; margin-bottom:14px; }
  .meta b { color:#374151; font-weight:600; }
  .accent { height:4px; background:#0052FF; border-radius:2px; margin:10px 0 16px; }
  table { width:100%; border-collapse:collapse; font-size:11.5px; }
  th, td { border:1px solid #e5e7eb; padding:7px 10px; }
  thead th { background:#0052FF; color:#fff; font-weight:600; text-align:center; }
  thead th.rk { text-align:left; }
  tbody th.rk { text-align:left; font-weight:600; background:#fafbfc; }
  td.num, th.num { text-align:center; font-variant-numeric:tabular-nums; }
  td.tot { font-weight:700; background:#f3f4f6; }
  tbody tr:nth-child(even) td { background:#fafbfc; }
  tbody tr:nth-child(even) td.tot { background:#eef0f2; }
  .z { color:#d1d5db; }
  tfoot td, tfoot th { background:#e5e7eb; font-weight:700; text-align:center; }
  tfoot th { text-align:left; }
  .foot { margin-top:16px; font-size:9px; color:#9ca3af; text-align:right; }
</style></head>
<body>
  <div class="brand">Sensbey Web App</div>
  <h1>${esc(t('hist.pdf.heading', { row: rowLabel }))}</h1>
  <div class="meta">
    <b>${esc(t('hist.pdf.cycle'))}</b>：${esc(cycleLabel)}　<b>${esc(t('hist.pdf.type'))}</b>：${esc(typeLabel)}　<b>${esc(t('hist.pdf.dept'))}</b>：${esc(deptLabel)}<br>
    <b>${esc(t('hist.pdf.total'))}</b>：${pivot.colTotals._total} ${esc(t('hist.pdf.countSuffix'))}　<b>${esc(t('hist.pdf.generated'))}</b>：${gen}
  </div>
  <div class="accent"></div>
  <table>
    <thead><tr><th class="rk">${esc(rowLabel)}</th>${headCols}<th class="num">${esc(t('hist.pivot.total'))}</th></tr></thead>
    <tbody>${bodyRows}</tbody>
    <tfoot><tr><th class="rk">${esc(t('hist.pivot.total'))}</th>${footCols}<td class="num">${pivot.colTotals._total}</td></tr></tfoot>
  </table>
  <div class="foot">© Sensbey Web App（統合業務プラットフォーム）— ${esc(t('hist.pdf.docTitle'))}</div>
</body></html>`;

    const w = openPrintWindow(html, { delay: 600 });
    if (!w) toast.error(t('hist.pdf.popupBlocked'));
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="pivot-view">
      {/* Pivot row dimension picker */}
      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50/60 flex items-center gap-3">
        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('hist.pivot.row')}</span>
        <div className="inline-flex items-center bg-white border border-zinc-200 rounded-md p-0.5">
          {[
            { value: 'employee',   key: 'hist.pivot.applicant' },
            { value: 'department', key: 'hist.pivot.dept' },
            { value: 'month',      key: 'hist.pivot.month' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPivotRow(opt.value)}
              data-testid={`pivot-row-${opt.value}`}
              className={`h-7 px-3 text-xs font-medium rounded transition-colors ${
                pivotRow === opt.value ? 'bg-teal-700 text-white shadow-sm' : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              {t(opt.key)}
            </button>
          ))}
        </div>
        <span className="text-xs text-zinc-400">×</span>
        <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('hist.pivot.col')}</span>
        <span className="text-xs text-zinc-700 font-medium">{t('hist.pivot.colTypes')}</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-zinc-400 hidden md:inline">{t('hist.pivot.cellHint')}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={exportPivotPdf}
            data-testid="pivot-export-pdf-btn"
            className="bg-white text-rose-700 border-rose-300 hover:bg-rose-50"
          >
            <FilePdf size={14} className="mr-1" /> {t('hist.pivot.pdfBtn')}
          </Button>
        </div>
      </div>

      {pivot.rows.length === 0 ? (
        <p className="px-4 py-12 text-center text-sm text-zinc-500">{t('hist.pivot.noData')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/80 border-b border-zinc-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-zinc-600 uppercase tracking-wider sticky left-0 bg-zinc-50/95 z-10">
                  {pivotRow === 'employee' ? t('hist.pivot.applicant') : pivotRow === 'department' ? t('hist.pivot.dept') : t('hist.pivot.month')}
                </th>
                {PIVOT_FORM_COLS.map((c) => (
                  <th key={c.value} className="px-3 py-2.5 text-center text-[11px] font-semibold text-zinc-600 uppercase tracking-wider whitespace-nowrap">
                    {t(c.key)}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-center text-[11px] font-bold text-zinc-700 uppercase tracking-wider bg-zinc-100/80">
                  {t('hist.pivot.total')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {pivot.rows.map((row) => (
                <tr key={row.key} className="hover:bg-teal-50/50" data-testid={`pivot-row-${row.key}`}>
                  <td className="px-4 py-2.5 text-zinc-900 font-medium sticky left-0 bg-white">
                    {row.key}
                  </td>
                  {PIVOT_FORM_COLS.map((c) => {
                    const n = row[c.value] || 0;
                    return (
                      <td key={c.value} className="px-3 py-2.5 text-center tabular-nums">
                        {n > 0 ? (
                          <button
                            type="button"
                            onClick={() => onDrillDown(row.key, c.value)}
                            data-testid={`pivot-cell-${row.key}-${c.value}`}
                            className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded text-sm font-semibold text-teal-700 hover:bg-teal-700/10 transition-colors"
                            title={t('hist.pivot.drillTip', { row: row.key, col: t(c.key), n })}
                          >
                            {n}
                          </button>
                        ) : (
                          <span className="text-zinc-300">·</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-center tabular-nums font-bold text-zinc-900 bg-zinc-50/60">
                    <button
                      type="button"
                      onClick={() => onDrillDown(row.key, null)}
                      className="hover:underline"
                      data-testid={`pivot-cell-${row.key}-total`}
                    >
                      {row._total}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-zinc-100/80 border-t-2 border-zinc-200">
              <tr>
                <td className="px-4 py-2.5 text-zinc-700 font-bold text-xs uppercase tracking-wider sticky left-0 bg-zinc-100/95">{t('hist.pivot.total')}</td>
                {PIVOT_FORM_COLS.map((c) => (
                  <td key={c.value} className="px-3 py-2.5 text-center tabular-nums font-bold text-zinc-900">
                    {pivot.colTotals[c.value] || 0}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-center tabular-nums font-bold text-zinc-900 bg-zinc-200/60">
                  {pivot.colTotals._total}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
